import { useMemo } from 'react';
import tw, { styled } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { useAtom } from 'jotai';
import { PlusIcon } from '@heroicons/react/24/outline';

import { ButtonV2, Link, SkeletonEmployerCardListLoading, Title, TitleAndButtonWrapper } from '@restworld/ui-ds';

import useEmployerJobListings from '../../../../job-listing/hooks/use-employer-job-listing';
import EmployerJobListingCardsList from '../../../../job-listing/employer-job-listing-cards-list';
import { useEmployerRestaurantDetails } from '../../../hooks/use-employer-restuarant-details';
import { showContactRestWorldManagerModalAtom } from '../../../employer-restaurants-filters/employer-restaurants.atoms';
import EmployerNoResults from '../../../../employer-no-results';

function RestaurantOpenJobPositionsSection({ isDashboardView }: { isDashboardView?: boolean }) {
  const { t } = useTranslation();
  const restaurantDetails = useEmployerRestaurantDetails();
  const [, setShowContactRestworldManagerModal] = useAtom(showContactRestWorldManagerModalAtom);

  const queryConfig = useMemo(
    () =>
      isDashboardView
        ? { status: 'online' }
        : {
            restaurantName: restaurantDetails?.restaurant_name,
            status: 'online',
            enabled: !!restaurantDetails,
          },
    [isDashboardView, restaurantDetails]
  );

  const { jobPositionItems, query, hasNextPage, loading } = useEmployerJobListings(queryConfig);

  if (isDashboardView && !query.isLoading && !jobPositionItems?.length) return null;

  return (
    <SectionWrapper>
      <HeaderWrapper>
        <TitleAndButtonWrapper>
          <Title
            textColor="neutral-700"
            fontWeight="semibold"
            twStyle={isDashboardView ? tw`text-base` : tw`text-lg lg:text-xl`}
          >
            {isDashboardView ? t('dashboard-page:your_job_positions') : t('restaurant-details-page:open_positions')}
          </Title>
        </TitleAndButtonWrapper>
        {!isDashboardView && (
          <Link StartIcon={PlusIcon} onClick={() => setShowContactRestworldManagerModal(true)} newTab={false}>
            {t('restaurant-details-page:open_a_new_position')}
          </Link>
        )}
      </HeaderWrapper>
      <CardsWrapper>
        {loading ? (
          <>
            <SkeletonEmployerCardListLoading />
            <SkeletonEmployerCardListLoading />
          </>
        ) : !jobPositionItems.length ? (
          <NoResultsWrapper>
            <EmployerNoResults
              title={t('restaurant-details-page:currently_you_have_no_open_job_positions')}
              content={t('restaurant-details-page:create_a_new_job_posting_so_we_can_send_candidates')}
              contentStyle={tw`max-w-screen-sm`}
            />
          </NoResultsWrapper>
        ) : (
          <EmployerJobListingCardsList jobPositionItems={jobPositionItems} />
        )}
      </CardsWrapper>
      <LoadMoreBtnWrapper>
        {hasNextPage && (
          <ButtonV2
            StartIcon={PlusIcon}
            variant="tertiary"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
            twStyle={tw`mt-16`}
          >
            {t('load_more_items')}
          </ButtonV2>
        )}
      </LoadMoreBtnWrapper>
    </SectionWrapper>
  );
}
export default RestaurantOpenJobPositionsSection;

const SectionWrapper = styled.section`
  ${tw`mt-16`}
`;
const HeaderWrapper = styled.div`
  ${tw`flex justify-between mb-4`}
`;
const CardsWrapper = styled.div`
  ${tw`flex flex-col gap-4`}
`;
const LoadMoreBtnWrapper = styled.div`
  ${tw`flex justify-center`}
`;
const NoResultsWrapper = styled.div`
  ${tw`rounded-2xl border border-neutral-40 py-5 lg:py-8`}
`;
